import React, { useState } from 'react';
import { 
  Plus, Search, Edit2, Trash2, AlertTriangle, Shield, FileText, 
  Camera, Clock, CheckCircle, XCircle, Eye, Calendar, User,
  Building2, MapPin, DollarSign, Phone, Mail, Upload, Download,
  Paperclip, Image, AlertCircle, Info, Zap, X, UserPlus
} from 'lucide-react';
import { DamageTheftRecord } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { showGlobalNotification } from '../hooks/useNotifications';
import { apiService } from '../services/api';
import Modal from './Modal';

const DamageTheftPage: React.FC = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState<DamageTheftRecord | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<DamageTheftRecord | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [recordsData, setRecordsData] = useState<DamageTheftRecord[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [computers, setComputers] = useState<any[]>([]);
  const [mobilePhones, setMobilePhones] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Load data on component mount
  React.useEffect(() => {
    loadData();
  }, []);

  // Bildirimden belirli bir kayıt açma
  React.useEffect(() => {
    const handler = async (e: CustomEvent) => {
      if (e.detail?.page !== 'damage-theft' || !e.detail?.entityId) return;
      try {
        const record = await apiService.getDamageTheftRecordById(e.detail.entityId);
        if (record) { setSelectedRecord(record); setShowDetailModal(true); }
      } catch (err) { console.error('[openEntityById damagetheft]', err); }
    };
    window.addEventListener('openEntityById', handler as EventListener);
    return () => window.removeEventListener('openEntityById', handler as EventListener);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [recordsResponse, companiesResponse, locationsResponse, usersResponse, devicesResponse] = await Promise.all([
        apiService.getDamageTheftRecords(),
        apiService.getCompanies(),
        apiService.getLocations(),
        apiService.getUsers(),
        apiService.getDamageTheftDevices()
      ]);
      
      setRecordsData(Array.isArray(recordsResponse) ? recordsResponse : []);
      setCompanies(Array.isArray(companiesResponse) ? companiesResponse : []);
      setLocations(Array.isArray(locationsResponse) ? locationsResponse : []);
      setAllUsers(Array.isArray(usersResponse) ? usersResponse : []);
      setComputers(Array.isArray(devicesResponse?.computers) ? devicesResponse.computers : []);
      setMobilePhones(Array.isArray(devicesResponse?.mobilePhones) ? devicesResponse.mobilePhones : []);
    } catch (error) {
      console.error('Error loading data:', error);
      showGlobalNotification.error('Hata', 'Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  
  const [recordData, setRecordData] = useState({
    record_number: '',
    company_id: 0,
    location_id: 0,
    user_id: 0,
    is_disabled_employee: false,
    incident_type: 'damage' as DamageTheftRecord['incident_type'],
    device_name: '',
    device_type: 'pc' as 'pc' | 'mobile_phone' | 'monitor' | 'tablet' | 'printer' | 'other',
    selected_computer_id: 0,
    selected_mobile_phone_id: 0,
    device_brand: '',
    device_model: '',
    serial_number: '',
    inventory_number: '',
    purchase_date: '',
    purchase_price: 0,
    currency: 'TRY' as 'TRY' | 'USD' | 'EUR',
    incident_date: '',
    incident_description: '',
    damage_assessment: '',
    is_repairable: true,
    estimated_repair_cost: 0,
    replacement_cost: 0,
    current_market_price: 0,
    calculated_deduction: 0,
    insurance_claim_number: '',
    insurance_company: '',
    police_report_number: '',
    status: 'reported' as DamageTheftRecord['status'],
    priority: 'normal' as DamageTheftRecord['priority'],
    responsible_person_id: 0,
    witness_names: '',
    photos_attached: false,
    documents_attached: false,
    has_insurance: false,
    resolution_notes: '',
    notes: ''
  });

  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    phone: '',
    title: '',
    department: '',
    location_id: 0
  });
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [computerSearchTerm, setComputerSearchTerm] = useState('');
  const [mobilePhoneSearchTerm, setMobilePhoneSearchTerm] = useState('');

  // Prosedüre göre kesinti hesaplama
  const calculateDeduction = () => {
    const purchasePrice = recordData.purchase_price || 0;
    const purchaseDate = recordData.purchase_date;
    const currentMarketPrice = recordData.current_market_price || 0;
    const estimatedRepairCost = recordData.estimated_repair_cost || 0;
    const deviceType = recordData.device_type;
    const incidentType = recordData.incident_type;
    const isRepairable = recordData.is_repairable;
    const isDisabledEmployee = recordData.is_disabled_employee;

    if (!purchaseDate || purchasePrice === 0) return 0;

    // Calculate usage years (not months!)
    const today = new Date();
    const purchase = new Date(purchaseDate);
    const monthsUsed = Math.floor((today.getTime() - purchase.getTime()) / (1000 * 60 * 60 * 24 * 30));
    const yearsUsed = Math.floor(monthsUsed / 12);

    // Determine percentage based on device type and years used
    let percentage = 0;

    if (deviceType === 'pc' || deviceType === 'monitor' || deviceType === 'printer' || deviceType === 'tablet' || deviceType === 'other') {
      // PC and similar devices percentages
      if (yearsUsed < 1) percentage = 0.85;
      else if (yearsUsed === 1) percentage = 0.85;
      else if (yearsUsed === 2) percentage = 0.70;
      else if (yearsUsed === 3) percentage = 0.50;
      else if (yearsUsed === 4) percentage = 0.40;
      else percentage = 0.30; // 5+ years
    } else if (deviceType === 'mobile_phone') {
      // Mobile phone percentages
      if (yearsUsed < 1) percentage = 0.70;
      else if (yearsUsed === 1) percentage = 0.70;
      else if (yearsUsed === 2) percentage = 0.40;
      else if (yearsUsed === 3) percentage = 0.20;
      else if (yearsUsed === 4) percentage = 0.15;
      else percentage = 0.10; // 5+ years
    }

    // Determine base price based on repairability and incident type
    let basePrice = purchasePrice;

    if (incidentType === 'damage') {
      // For damage cases: use repair cost if repairable, market price if not
      if (isRepairable) {
        basePrice = estimatedRepairCost || purchasePrice;
      } else {
        basePrice = currentMarketPrice || purchasePrice;
      }
    } else if (incidentType === 'theft' || incidentType === 'loss') {
      // For theft/loss: always use market price
      basePrice = currentMarketPrice || purchasePrice;
    }

    // Calculate deduction
    let deduction = basePrice * percentage;

    // Apply 50% discount for disabled employees
    if (isDisabledEmployee) {
      deduction = deduction * 0.50;
    }

    return Math.round(deduction);
  };

  const userDeduction = calculateDeduction();

  // Update calculated_deduction when relevant fields change
  React.useEffect(() => {
    const newDeduction = calculateDeduction();
    if (newDeduction !== recordData.calculated_deduction) {
      setRecordData(prev => ({ ...prev, calculated_deduction: newDeduction }));
    }
  }, [
    recordData.purchase_date,
    recordData.purchase_price,
    recordData.current_market_price,
    recordData.estimated_repair_cost,
    recordData.device_type,
    recordData.incident_type,
    recordData.is_repairable,
    recordData.is_disabled_employee
  ]);

  const handleAddUser = () => {
    setNewUserData({
      name: '',
      email: '',
      phone: '',
      title: '',
      department: '',
      location_id: recordData.location_id
    });
    setShowUserModal(true);
  };

  const handleSaveUser = () => {
    if (!newUserData.name || !newUserData.email || !newUserData.phone) {
      showGlobalNotification.error('Hata', 'Ad Soyad, E-posta ve Telefon alanları zorunludur');
      return;
    }

    // Simulate adding new user to the system
    const newUser = {
      id: Math.max(...allUsers.map(u => u.id)) + 1,
      name: newUserData.name,
      email: newUserData.email,
      phone: newUserData.phone,
      title: newUserData.title,
      department: newUserData.department,
      location_id: newUserData.location_id,
      location: locations.find(l => l.id === newUserData.location_id),
      role: 'calisan' as const,
      is_active: true,
      is_online: false,
      email_verified: false,
      two_factor_enabled: false,
      avatar: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    // Add to users list (in real app, this would be an API call)
    allUsers.push(newUser);
    
    // Set as responsible person
    setRecordData({...recordData, responsible_person_id: newUser.id});
    
    // Log activity
    logActivity(
      'create_user_quick',
      `${user?.name} hasar kaydı için "${newUserData.name}" kullanıcısını hızlı oluşturdu`,
      newUserData.name
    );
    
    setShowUserModal(false);
    showGlobalNotification.success('Başarılı', `${newUserData.name} kullanıcısı oluşturuldu ve sorumlu kişi olarak atandı`);
  };

  const incidentTypeOptions = [
    { value: 'damage', label: 'Hasar', color: 'bg-orange-100 text-orange-800', icon: AlertTriangle },
    { value: 'theft', label: 'Çalınma', color: 'bg-red-100 text-red-800', icon: Shield },
    { value: 'loss', label: 'Kayıp', color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle }
  ];

  const statusOptions = [
    { value: 'reported', label: 'Bildirildi', color: 'bg-primary/20 text-primary-dark' },
    { value: 'investigating', label: 'Araştırılıyor', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'insurance_claimed', label: 'Sigorta Başvurusu', color: 'bg-purple-100 text-purple-800' },
    { value: 'repaired', label: 'Onarıldı', color: 'bg-green-100 text-green-800' },
    { value: 'replaced', label: 'Değiştirildi', color: 'bg-indigo-100 text-indigo-800' },
    { value: 'written_off', label: 'Silindi', color: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' },
    { value: 'closed', label: 'Kapatıldı', color: 'bg-green-100 text-green-800' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Düşük', color: 'text-gray-600 dark:text-gray-400' },
    { value: 'normal', label: 'Normal', color: 'text-primary' },
    { value: 'high', label: 'Yüksek', color: 'text-orange-600' },
    { value: 'urgent', label: 'Acil', color: 'text-red-600' }
  ];

  const filteredRecords = recordsData.filter(record => {
    const matchesSearch = String(record.record_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         String(record.device_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         String(record.incident_description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         record.String(responsible_person || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || record.incident_type === filterType;
    const matchesStatus = filterStatus === 'all' || record.status === filterStatus;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  const logActivity = (action: string, description: string, recordNumber?: string) => {
    // Activity logging removed - using API
  };

  const handleEdit = (record: DamageTheftRecord) => {
    setEditingRecord(record);
    setRecordData({
      record_number: record.record_number,
      company_id: record.company_id,
      location_id: record.location_id,
      user_id: record.user_id,
      incident_type: record.incident_type,
      device_name: record.device_name,
      device_brand: record.device_brand || '',
      device_model: record.device_model || '',
      serial_number: record.serial_number || '',
      inventory_number: record.inventory_number || '',
      purchase_date: record.purchase_date || '',
      purchase_price: record.purchase_price || 0,
      currency: record.currency || 'TRY',
      incident_date: record.incident_date,
      incident_description: record.incident_description,
      damage_assessment: record.damage_assessment || '',
      is_repairable: record.is_repairable !== undefined ? record.is_repairable : true,
      estimated_repair_cost: record.estimated_repair_cost || 0,
      replacement_cost: record.replacement_cost || 0,
      current_market_price: record.current_market_price || 0,
      has_insurance: !!(record.insurance_company || record.insurance_claim_number),
      insurance_claim_number: record.insurance_claim_number || '',
      insurance_company: record.insurance_company || '',
      police_report_number: record.police_report_number || '',
      status: record.status,
      priority: record.priority,
      responsible_person_id: record.responsible_person_id || 0,
      witness_names: record.witness_names || '',
      is_disabled_employee: record.is_disabled_employee || false,
      device_type: record.device_type || 'pc',
      calculated_deduction: record.calculated_deduction || 0,
      photos_attached: record.photos_attached,
      documents_attached: record.documents_attached,
      resolution_notes: record.resolution_notes || '',
      notes: record.notes || ''
    });
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingRecord(null);
    const nextRecordNumber = `HAS-${new Date().getFullYear()}-${String(recordsData.length + 1).padStart(3, '0')}`;

    setRecordData({
      record_number: nextRecordNumber,
      company_id: 0,
      location_id: 0,
      user_id: user?.id || 0,
      incident_type: 'damage',
      device_name: '',
      device_brand: '',
      device_model: '',
      serial_number: '',
      inventory_number: '',
      purchase_date: '',
      purchase_price: 0,
      currency: 'TRY',
      incident_date: new Date().toISOString().split('T')[0],
      incident_description: '',
      damage_assessment: '',
      is_repairable: true,
      estimated_repair_cost: 0,
      replacement_cost: 0,
      current_market_price: 0,
      has_insurance: false,
      insurance_claim_number: '',
      insurance_company: '',
      police_report_number: '',
      status: 'reported',
      priority: 'normal',
      responsible_person_id: 0,
      witness_names: '',
      is_disabled_employee: false,
      device_type: 'pc' as 'pc' | 'mobile_phone' | 'monitor' | 'tablet' | 'printer' | 'other',
      calculated_deduction: 0,
      photos_attached: false,
      documents_attached: false,
      resolution_notes: '',
      notes: ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!recordData.record_number || !recordData.company_id || !recordData.location_id || !recordData.device_name || !recordData.incident_description) {
      showGlobalNotification.error('Hata', 'Zorunlu alanları doldurunuz');
      return;
    }

    try {
      // Dosya yükleme simülasyonu
      const fileUrls = uploadedFiles.map(file => URL.createObjectURL(file));

      // Prepare data - clear insurance fields if has_insurance is false
      const dataToSend = {
        ...recordData,
        insurance_company: recordData.has_insurance ? recordData.insurance_company : '',
        insurance_claim_number: recordData.has_insurance ? recordData.insurance_claim_number : ''
      };

      if (editingRecord) {
        await apiService.updateDamageTheftRecord(editingRecord.id, dataToSend);
        showGlobalNotification.success('Başarılı', 'Hasar/çalınma kaydı güncellendi');
      } else {
        await apiService.createDamageTheftRecord(dataToSend);
        showGlobalNotification.success('Başarılı', 'Hasar/çalınma kaydı oluşturuldu');
      }

      await loadData();
      setShowModal(false);
      setUploadedFiles([]);
      resetForm();
    } catch (error: any) {
      console.error('Error saving damage theft record:', error);
      showGlobalNotification.error('Hata', error.message || 'Kayıt kaydedilirken hata oluştu');
    }
  };

  const handleSaveOld = () => {
    if (editingRecord) {
      // Update existing record
      const updatedRecords = recordsData.map(rec => 
        rec.id === editingRecord.id 
          ? {
              ...rec,
              photos_attached: uploadedFiles.some(f => f.type.startsWith('image/')),
              documents_attached: uploadedFiles.some(f => !f.type.startsWith('image/')),
              record_number: recordData.record_number,
              company_id: recordData.company_id,
              location_id: recordData.location_id,
              user_id: recordData.user_id,
              incident_type: recordData.incident_type,
              device_name: recordData.device_name,
              device_brand: recordData.device_brand,
              device_model: recordData.device_model,
              serial_number: recordData.serial_number,
              inventory_number: recordData.inventory_number,
              purchase_date: recordData.purchase_date,
              purchase_price: recordData.purchase_price,
              currency: recordData.currency,
              incident_date: recordData.incident_date,
              incident_description: recordData.incident_description,
              damage_assessment: recordData.damage_assessment,
              estimated_repair_cost: recordData.estimated_repair_cost,
              replacement_cost: recordData.replacement_cost,
              insurance_claim_number: recordData.insurance_claim_number,
              insurance_company: recordData.insurance_company,
              police_report_number: recordData.police_report_number,
              status: recordData.status,
              priority: recordData.priority,
              responsible_person_id: recordData.responsible_person_id,
              witness_names: recordData.witness_names,
              resolution_notes: recordData.resolution_notes,
              notes: recordData.notes,
              company: companies.find(c => c.id === recordData.company_id),
              location: locations.find(l => l.id === recordData.location_id),
              user: allUsers.find(u => u.id === recordData.user_id),
              updated_at: new Date().toISOString()
            }
          : rec
      );
      setRecordsData(updatedRecords);
      
      logActivity(
        'update_damage_theft',
        `${user?.name} "${recordData.record_number}" hasar/çalınma kaydını güncelledi`,
        recordData.record_number
      );
    } else {
      // Add new record
      const newRecord: DamageTheftRecord = {
        id: Math.max(...recordsData.map(r => r.id)) + 1,
        record_number: recordData.record_number,
        photos_attached: uploadedFiles.some(f => f.type.startsWith('image/')),
        documents_attached: uploadedFiles.some(f => !f.type.startsWith('image/')),
        company_id: recordData.company_id,
        location_id: recordData.location_id,
        user_id: recordData.user_id,
        incident_type: recordData.incident_type,
        device_name: recordData.device_name,
        device_brand: recordData.device_brand,
        device_model: recordData.device_model,
        serial_number: recordData.serial_number,
        inventory_number: recordData.inventory_number,
        purchase_date: recordData.purchase_date,
        purchase_price: recordData.purchase_price,
        currency: recordData.currency,
        incident_date: recordData.incident_date,
        incident_description: recordData.incident_description,
        damage_assessment: recordData.damage_assessment,
        estimated_repair_cost: recordData.estimated_repair_cost,
        replacement_cost: recordData.replacement_cost,
        insurance_claim_number: recordData.insurance_claim_number,
        insurance_company: recordData.insurance_company,
        police_report_number: recordData.police_report_number,
        status: recordData.status,
        priority: recordData.priority,
        responsible_person_id: recordData.responsible_person_id,
        witness_names: recordData.witness_names,
        resolution_notes: recordData.resolution_notes,
        created_by: user?.id || 1,
        notes: recordData.notes,
        company: companies.find(c => c.id === recordData.company_id),
        location: locations.find(l => l.id === recordData.location_id),
        user: allUsers.find(u => u.id === recordData.user_id),
        created_by_user: user,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      setRecordsData([...recordsData, newRecord]);
      
      logActivity(
        'create_damage_theft',
        `${user?.name} "${recordData.record_number}" hasar/çalınma kaydını oluşturdu`,
        recordData.record_number
      );
    }
    
    setShowModal(false);
    setUploadedFiles([]);
    resetForm();
  };

  const handleDelete = async (id: number) => {
    const recordToDelete = recordsData.find(r => r.id === id);
    if (!recordToDelete) return;
    
    if (confirm('Bu hasar/çalınma kaydını silmek istediğinizden emin misiniz?')) {
      try {
        await apiService.deleteDamageTheftRecord(id);
        await loadData();
        showGlobalNotification.success('Başarılı', 'Hasar/çalınma kaydı silindi');
      } catch (error: any) {
        console.error('Error deleting damage theft record:', error);
        showGlobalNotification.error('Hata', error.message || 'Kayıt silinirken hata oluştu');
      }
    }
  };

  const showRecordDetail = (record: DamageTheftRecord) => {
    setSelectedRecord(record);
    setShowDetailModal(true);
  };

  const printDamageReport = (record: DamageTheftRecord) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Hasar/Çalınma Raporu - ${record.record_number}</title>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            margin: 20px; 
            line-height: 1.6;
            color: #333;
          }
          .header { 
            text-align: center; 
            border-bottom: 2px solid #333; 
            padding-bottom: 20px; 
            margin-bottom: 30px;
          }
          .company-logo {
            font-size: 24px;
            font-weight: bold;
            color: #2563eb;
            margin-bottom: 10px;
          }
          .report-title {
            font-size: 20px;
            font-weight: bold;
            margin: 10px 0;
          }
          .record-number {
            font-size: 16px;
            color: #666;
          }
          .section { 
            margin-bottom: 25px; 
            page-break-inside: avoid;
          }
          .section-title { 
            font-size: 16px; 
            font-weight: bold; 
            background-color: #f3f4f6; 
            padding: 8px 12px; 
            border-left: 4px solid #2563eb;
            margin-bottom: 15px;
          }
          .info-grid { 
            display: grid; 
            grid-template-columns: 1fr 1fr; 
            gap: 15px; 
            margin-bottom: 15px;
          }
          .info-item { 
            display: flex; 
            justify-content: space-between; 
            padding: 8px 0; 
            border-bottom: 1px solid #e5e7eb;
          }
          .info-label { 
            font-weight: bold; 
            color: #374151;
            min-width: 120px;
          }
          .info-value { 
            color: #111827;
            text-align: right;
          }
          .description-box {
            background-color: #f9fafb;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            padding: 15px;
            margin: 10px 0;
            white-space: pre-wrap;
          }
          .status-badge {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: bold;
            text-transform: uppercase;
          }
          .status-reported { background-color: #dbeafe; color: #1e40af; }
          .status-investigating { background-color: #fef3c7; color: #92400e; }
          .status-resolved { background-color: #d1fae5; color: #065f46; }
          .footer { 
            margin-top: 40px; 
            padding-top: 20px; 
            border-top: 1px solid #e5e7eb; 
            text-align: center; 
            font-size: 12px; 
            color: #6b7280;
          }
          .signature-section {
            margin-top: 40px;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 40px;
          }
          .signature-box {
            text-align: center;
            border-top: 1px solid #333;
            padding-top: 10px;
            margin-top: 60px;
          }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-logo">IT TOOLBOX</div>
          <div class="report-title">HASAR & ÇALINMA RAPORU</div>
          <div class="record-number">Kayıt No: ${record.record_number}</div>
        </div>

        <div class="section">
          <div class="section-title">Genel Bilgiler</div>
          <div class="info-grid">
            <div>
              <div class="info-item">
                <span class="info-label">Kayıt No:</span>
                <span class="info-value">${record.record_number}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Olay Tipi:</span>
                <span class="info-value">${
                  record.incident_type === 'damage' ? 'Hasar' :
                  record.incident_type === 'theft' ? 'Çalınma' : 'Kayıp'
                }</span>
              </div>
              <div class="info-item">
                <span class="info-label">Olay Tarihi:</span>
                <span class="info-value">${new Date(record.incident_date).toLocaleDateString('tr-TR')}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Durum:</span>
                <span class="info-value">
                  <span class="status-badge status-${record.status}">
                    ${statusOptions.find(s => s.value === record.status)?.label || record.status}
                  </span>
                </span>
              </div>
            </div>
            <div>
              <div class="info-item">
                <span class="info-label">Şirket:</span>
                <span class="info-value">${record.company?.name || '-'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Lokasyon:</span>
                <span class="info-value">${record.location?.name || '-'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Sorumlu Kişi:</span>
                <span class="info-value">${record.responsible_person?.name || '-'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Öncelik:</span>
                <span class="info-value">${
                  record.priority === 'urgent' ? 'Acil' :
                  record.priority === 'high' ? 'Yüksek' :
                  record.priority === 'normal' ? 'Normal' : 'Düşük'
                }</span>
              </div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Cihaz Bilgileri</div>
          <div class="info-grid">
            <div>
              <div class="info-item">
                <span class="info-label">Cihaz Adı:</span>
                <span class="info-value">${record.device_name}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Marka:</span>
                <span class="info-value">${record.device_brand || '-'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Model:</span>
                <span class="info-value">${record.device_model || '-'}</span>
              </div>
            </div>
            <div>
              <div class="info-item">
                <span class="info-label">Seri No:</span>
                <span class="info-value">${record.serial_number || '-'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Envanter No:</span>
                <span class="info-value">${record.inventory_number || '-'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Satın Alma Tarihi:</span>
                <span class="info-value">${record.purchase_date ? new Date(record.purchase_date).toLocaleDateString('tr-TR') : '-'}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Olay Açıklaması</div>
          <div class="description-box">${record.incident_description}</div>
        </div>

        ${record.damage_assessment ? `
        <div class="section">
          <div class="section-title">Hasar Değerlendirmesi</div>
          <div class="description-box">${record.damage_assessment}</div>
        </div>
        ` : ''}

        <div class="section">
          <div class="section-title">Maliyet Bilgileri</div>
          <div class="info-grid">
            <div>
              ${record.purchase_price ? `
              <div class="info-item">
                <span class="info-label">Satın Alma Fiyatı:</span>
                <span class="info-value">${getCurrencySymbol(record.currency || 'TRY')}${(record.purchase_price || 0).toLocaleString()}</span>
              </div>
              ` : ''}
              ${record.estimated_repair_cost ? `
              <div class="info-item">
                <span class="info-label">Tahmini Onarım:</span>
                <span class="info-value">${getCurrencySymbol(record.currency || 'TRY')}${(record.estimated_repair_cost || 0).toLocaleString()}</span>
              </div>
              ` : ''}
            </div>
            <div>
              ${record.replacement_cost ? `
              <div class="info-item">
                <span class="info-label">Değiştirme Maliyeti:</span>
                <span class="info-value">${getCurrencySymbol(record.currency || 'TRY')}${(record.replacement_cost || 0).toLocaleString()}</span>
              </div>
              ` : ''}
              ${calculateDeduction(record) > 0 ? `
              <div class="info-item">
                <span class="info-label">Kullanıcıdan Kesinti:</span>
                <span class="info-value" style="color: #dc2626; font-weight: bold;">${getCurrencySymbol(record.currency || 'TRY')}${(calculateDeduction(record) || 0).toLocaleString()}</span>
              </div>
              ` : ''}
            </div>
          </div>
        </div>

        ${(record.insurance_company || record.police_report_number) ? `
        <div class="section">
          <div class="section-title">Sigorta ve Hukuki Bilgiler</div>
          <div class="info-grid">
            <div>
              ${record.insurance_company ? `
              <div class="info-item">
                <span class="info-label">Sigorta Şirketi:</span>
                <span class="info-value">${record.insurance_company}</span>
              </div>
              ` : ''}
              ${record.insurance_claim_number ? `
              <div class="info-item">
                <span class="info-label">Sigorta Başvuru No:</span>
                <span class="info-value">${record.insurance_claim_number}</span>
              </div>
              ` : ''}
            </div>
            <div>
              ${record.police_report_number ? `
              <div class="info-item">
                <span class="info-label">Polis Rapor No:</span>
                <span class="info-value">${record.police_report_number}</span>
              </div>
              ` : ''}
              ${record.witness_names ? `
              <div class="info-item">
                <span class="info-label">Tanıklar:</span>
                <span class="info-value">${record.witness_names}</span>
              </div>
              ` : ''}
            </div>
          </div>
        </div>
        ` : ''}

        ${record.resolution_notes ? `
        <div class="section">
          <div class="section-title">Çözüm Notları</div>
          <div class="description-box">${record.resolution_notes}</div>
        </div>
        ` : ''}

        <div class="section">
          <div class="section-title">Ek Bilgiler</div>
          <div class="info-grid">
            <div>
              <div class="info-item">
                <span class="info-label">Fotoğraf Eklendi:</span>
                <span class="info-value">${record.photos_attached ? 'Evet' : 'Hayır'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Belge Eklendi:</span>
                <span class="info-value">${record.documents_attached ? 'Evet' : 'Hayır'}</span>
              </div>
            </div>
            <div>
              <div class="info-item">
                <span class="info-label">Kayıt Tarihi:</span>
                <span class="info-value">${new Date(record.created_at).toLocaleString('tr-TR')}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Kaydeden:</span>
                <span class="info-value">${record.created_by_user?.name || '-'}</span>
              </div>
            </div>
          </div>
        </div>

        ${record.notes ? `
        <div class="section">
          <div class="section-title">Notlar</div>
          <div class="description-box">${record.notes}</div>
        </div>
        ` : ''}

        <div class="signature-section">
          <div class="signature-box">
            <div>Sorumlu Kişi İmzası</div>
            <div style="margin-top: 5px; font-size: 12px;">${record.responsible_person?.name || ''}</div>
          </div>
          <div class="signature-box">
            <div>IT Sorumlusu İmzası</div>
            <div style="margin-top: 5px; font-size: 12px;">${record.created_by_user?.name || ''}</div>
          </div>
        </div>

        <div class="footer">
          <p>Bu rapor IT Toolbox sistemi tarafından ${new Date().toLocaleString('tr-TR')} tarihinde oluşturulmuştur.</p>
          <p>© 2024 IT Toolbox - Kurumsal IT Yönetim Sistemi</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();

    // Log the print action
    logActivity(
      'print_damage_report',
      `${user?.name} "${record.record_number}" hasar/çalınma raporunu yazdırdı`,
      record.record_number
    );

    showGlobalNotification.success(
      'Rapor Yazdırıldı',
      `${record.record_number} numaralı rapor yazdırma için hazırlandı`
    );
  };

  const resetForm = () => {
    setRecordData({
      record_number: '',
      company_id: 0,
      location_id: 0,
      user_id: 0,
      incident_type: 'damage',
      device_name: '',
      device_brand: '',
      device_model: '',
      serial_number: '',
      inventory_number: '',
      purchase_date: '',
      purchase_price: 0,
      currency: 'TRY',
      incident_date: '',
      incident_description: '',
      damage_assessment: '',
      is_repairable: true,
      estimated_repair_cost: 0,
      replacement_cost: 0,
      current_market_price: 0,
      has_insurance: false,
      insurance_claim_number: '',
      insurance_company: '',
      police_report_number: '',
      status: 'reported',
      priority: 'normal',
      responsible_person_id: 0,
      witness_names: '',
      is_disabled_employee: false,
      device_type: 'pc' as 'pc' | 'mobile_phone' | 'monitor' | 'tablet' | 'printer' | 'other',
      calculated_deduction: 0,
      photos_attached: false,
      documents_attached: false,
      resolution_notes: '',
      notes: ''
    });
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    // Dosya boyutu kontrolü (max 10MB per file)
    const maxSize = 10 * 1024 * 1024;
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        showGlobalNotification.error('Hata', `${file.name} dosyası çok büyük (max 10MB)`);
        return false;
      }
      return true;
    });
    
    // Desteklenen dosya türleri
    const supportedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif',
      'application/pdf', 'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];
    
    const validTypeFiles = validFiles.filter(file => {
      if (!supportedTypes.includes(file.type)) {
        showGlobalNotification.error('Hata', `${file.name} desteklenmeyen dosya türü`);
        return false;
      }
      return true;
    });
    
    setUploadedFiles(prev => [...prev, ...validTypeFiles]);
    
    if (validTypeFiles.length > 0) {
      showGlobalNotification.success('Başarılı', `${validTypeFiles.length} dosya yüklendi`);
      
      // Log file upload
      if (user) {
        logActivity(
          'upload_files',
          `${user.name} hasar kaydına ${validTypeFiles.length} dosya yükledi`,
          recordData.record_number
        );
      }
    }
  };

  const removeFile = (index: number) => {
    const removedFile = uploadedFiles[index];
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    showGlobalNotification.info('Dosya Kaldırıldı', `${removedFile.name} kaldırıldı`);
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return Camera;
    if (fileType === 'application/pdf') return FileText;
    return FileText;
  };

  const getTypeColor = (type: string) => {
    return incidentTypeOptions.find(t => t.value === type)?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };

  const getTypeIcon = (type: string) => {
    return incidentTypeOptions.find(t => t.value === type)?.icon || AlertTriangle;
  };

  const getStatusColor = (status: string) => {
    return statusOptions.find(s => s.value === status)?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };

  const getPriorityColor = (priority: string) => {
    return priorityOptions.find(p => p.value === priority)?.color || 'text-gray-600 dark:text-gray-400';
  };

  const getCurrencySymbol = (currency: string) => {
    switch (currency) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'TRY': return '₺';
      default: return '₺';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          <span className="ml-2 text-gray-600 dark:text-gray-400">Hasar/çalınma kayıtları yükleniyor...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Hasar & Çalınma</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">Cihaz hasar ve çalınma kayıtlarını yönetin</p>
        </div>
        <button
          onClick={handleAdd}
          className="flex items-center justify-center px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors min-h-[44px] text-sm sm:text-base w-full sm:w-auto"
        >
          <Plus className="w-4 h-4 mr-2" />
          Yeni Kayıt
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 min-h-[80px] sm:min-h-[100px]">
          <div className="flex flex-col sm:flex-row items-center justify-between h-full">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Toplam Kayıt</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-gray-100">{recordsData.length}</p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-red-100 rounded-lg flex items-center justify-center mt-2 sm:mt-0">
              <AlertTriangle className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 min-h-[80px] sm:min-h-[100px]">
          <div className="flex flex-col sm:flex-row items-center justify-between h-full">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Hasar</p>
              <p className="text-lg sm:text-2xl font-bold text-orange-600">
                {recordsData.filter(r => r.incident_type === 'damage').length}
              </p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-orange-100 rounded-lg flex items-center justify-center mt-2 sm:mt-0">
              <AlertTriangle className="w-4 h-4 sm:w-6 sm:h-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 min-h-[80px] sm:min-h-[100px]">
          <div className="flex flex-col sm:flex-row items-center justify-between h-full">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Çalınma</p>
              <p className="text-lg sm:text-2xl font-bold text-red-600">
                {recordsData.filter(r => r.incident_type === 'theft').length}
              </p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-red-100 rounded-lg flex items-center justify-center mt-2 sm:mt-0">
              <Shield className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 min-h-[80px] sm:min-h-[100px] col-span-2 sm:col-span-1">
          <div className="flex flex-col sm:flex-row items-center justify-between h-full">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Toplam Zarar</p>
              <p className="text-lg sm:text-2xl font-bold text-purple-600">
                ₺{recordsData.reduce((sum, r) => sum + (r.replacement_cost || 0), 0).toLocaleString()}
              </p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center mt-2 sm:mt-0">
              <DollarSign className="w-4 h-4 sm:w-6 sm:h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Kayıt ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>
        
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="all">Tüm Tipler</option>
          {incidentTypeOptions.map(type => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="all">Tüm Durumlar</option>
          {statusOptions.map(status => (
            <option key={status.value} value={status.value}>{status.label}</option>
          ))}
        </select>
      </div>

      {/* Records Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredRecords.map((record) => {
          const TypeIcon = getTypeIcon(record.incident_type);
          return (
            <div
              key={record.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <TypeIcon className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {record.record_number}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{record.device_name}</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => showRecordDetail(record)}
                    className="p-1 text-gray-400 hover:text-primary transition-colors"
                    title="Detayları Görüntüle"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(record)}
                    className="p-1 text-gray-400 hover:text-primary transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(record.id)}
                    className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center">
                  <Building2 className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">{record.company?.name}</span>
                </div>

                <div className="flex items-center">
                  <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">{record.location?.name}</span>
                </div>

                <div className="flex items-center">
                  <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {new Date(record.incident_date).toLocaleDateString('tr-TR')}
                  </span>
                </div>

                {record.inventory_number && (
                  <div className="flex items-center">
                    <FileText className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Envanter: {record.inventory_number}</span>
                  </div>
                )}

                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                  {record.incident_description}
                </p>

                <div className="flex items-center justify-between">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(record.incident_type)}`}>
                    {incidentTypeOptions.find(t => t.value === record.incident_type)?.label}
                  </span>
                  <span className={`text-sm font-medium ${getPriorityColor(record.priority)}`}>
                    {priorityOptions.find(p => p.value === record.priority)?.label}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(record.status)}`}>
                    {statusOptions.find(s => s.value === record.status)?.label}
                  </span>
                  {record.replacement_cost && (
                    <span className="text-sm font-semibold text-red-600">
                      {getCurrencySymbol(record.currency || 'TRY')}{(record.replacement_cost || 0).toLocaleString()}
                    </span>
                  )}
                </div>

                <div className="flex justify-between items-center pt-2 border-t border-gray-100 dark:border-gray-700">
                  <div className="flex space-x-2">
                    {record.photos_attached && (
                      <Camera className="w-4 h-4 text-green-600" title="Fotoğraf var" />
                    )}
                    {record.documents_attached && (
                      <Paperclip className="w-4 h-4 text-primary" title="Belge var" />
                    )}
                  </div>
                  <span className="text-xs text-gray-400 dark:text-gray-500 dark:text-gray-400">
                    {new Date(record.created_at).toLocaleDateString('tr-TR')}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Record Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[95vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {editingRecord ? 'Hasar/Çalınma Kaydı Düzenle' : 'Yeni Hasar/Çalınma Kaydı'}
              </h2>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Temel Bilgiler</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Kayıt Numarası *
                    </label>
                    <input
                      type="text"
                      value={recordData.record_number}
                      onChange={(e) => setRecordData({...recordData, record_number: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="HAS-2024-001"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Olay Tipi *
                    </label>
                    <select
                      value={recordData.incident_type}
                      onChange={(e) => setRecordData({...recordData, incident_type: e.target.value as DamageTheftRecord['incident_type']})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      {incidentTypeOptions.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Şirket *
                    </label>
                    <select
                      value={recordData.company_id}
                      onChange={(e) => setRecordData({...recordData, company_id: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value={0}>Şirket seçin</option>
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Lokasyon *
                    </label>
                    <select
                      value={recordData.location_id}
                      onChange={(e) => setRecordData({...recordData, location_id: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value={0}>Lokasyon seçin</option>
                      {locations.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Olay Tarihi *
                    </label>
                    <input
                      type="date"
                      value={recordData.incident_date}
                      onChange={(e) => setRecordData({...recordData, incident_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Öncelik *
                    </label>
                    <select
                      value={recordData.priority}
                      onChange={(e) => setRecordData({...recordData, priority: e.target.value as DamageTheftRecord['priority']})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      {priorityOptions.map((priority) => (
                        <option key={priority.value} value={priority.value}>
                          {priority.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Responsible Person Section */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Zimmetlenen Çalışan</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Zimmetlenen Çalışan *
                    </label>
                    <div className="space-y-2">
                      <input
                        type="text"
                        placeholder="Çalışan ara..."
                        value={userSearchTerm}
                        onChange={(e) => setUserSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                      <div className="flex space-x-2">
                        <select
                          value={recordData.responsible_person_id}
                          onChange={(e) => {
                            const userId = parseInt(e.target.value);
                            setRecordData({...recordData, responsible_person_id: userId});
                            
                            // If device type is selected and user is selected, clear device selections to re-filter
                            if ((recordData.device_type === 'pc' || recordData.device_type === 'mobile_phone') && userId > 0) {
                              setRecordData(prev => ({
                                ...prev, 
                                responsible_person_id: userId,
                                selected_computer_id: 0,
                                selected_mobile_phone_id: 0,
                                device_name: '',
                                device_brand: '',
                                device_model: '',
                                serial_number: '',
                                inventory_number: '',
                                purchase_date: '',
                                purchase_price: 0
                              }));
                            }
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <option value={0}>Çalışan seçin</option>
                          {allUsers
                            .filter(u => Number(u.is_active) === 1)
                            .filter(user => 
                              userSearchTerm === '' || 
                              user.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                              (user.title && user.title.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
                              (user.role && user.role.toLowerCase().includes(userSearchTerm.toLowerCase()))
                            )
                            .map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.name} - {user.title || user.role}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={handleAddUser}
                          className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center"
                          title="Hızlı Kullanıcı Oluştur"
                        >
                          <UserPlus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Device Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Cihaz Bilgileri</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Cihaz Tipi *
                    </label>
                    <select
                      value={recordData.device_type}
                      onChange={(e) => {
                        const newType = e.target.value as any;
                        setRecordData({
                          ...recordData, 
                          device_type: newType,
                          selected_computer_id: 0,
                          selected_mobile_phone_id: 0,
                          device_name: '',
                          device_brand: '',
                          device_model: '',
                          serial_number: '',
                          inventory_number: '',
                          purchase_date: '',
                          purchase_price: 0
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">Cihaz tipi seçin</option>
                      <option value="pc">Bilgisayar (PC)</option>
                      <option value="mobile_phone">Cep Telefonu</option>
                      <option value="monitor">Monitör</option>
                      <option value="tablet">Tablet</option>
                      <option value="printer">Yazıcı</option>
                      <option value="other">Diğer</option>
                    </select>
                  </div>

                  {recordData.device_type === 'pc' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Bilgisayar Seçin *
                      </label>
                      <div className="space-y-2">
                        <input
                          type="text"
                          placeholder="Bilgisayar ara..."
                          value={computerSearchTerm}
                          onChange={(e) => setComputerSearchTerm(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                        <select
                          value={recordData.selected_computer_id}
                          onChange={(e) => {
                            const computerId = parseInt(e.target.value);
                            const computer = computers.find(c => c.id === computerId);
                            if (computer) {
                              setRecordData({
                                ...recordData,
                                selected_computer_id: computerId,
                                device_name: `${computer.brand_name || ''} ${computer.model_name || ''}`.trim(),
                                device_brand: computer.brand_name || '',
                                device_model: computer.model_name || '',
                                serial_number: computer.serial_number || '',
                                inventory_number: computer.inventory_number || '',
                                purchase_date: computer.purchase_date || '',
                                purchase_price: computer.purchase_price || 0
                              });
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <option value={0}>Bilgisayar seçin</option>
                          {computers
                            .filter(c => recordData.responsible_person_id === 0 || c.assigned_user_id === recordData.responsible_person_id)
                            .filter(computer => 
                              computerSearchTerm === '' ||
                              (computer.brand_name && computer.brand_name.toLowerCase().includes(computerSearchTerm.toLowerCase())) ||
                              (computer.model_name && computer.model_name.toLowerCase().includes(computerSearchTerm.toLowerCase())) ||
                              (computer.serial_number && computer.serial_number.toLowerCase().includes(computerSearchTerm.toLowerCase())) ||
                              (computer.inventory_number && computer.inventory_number.toLowerCase().includes(computerSearchTerm.toLowerCase())) ||
                              (computer.assigned_user_name && computer.assigned_user_name.toLowerCase().includes(computerSearchTerm.toLowerCase()))
                            )
                            .map((computer) => (
                            <option key={computer.id} value={computer.id}>
                              {computer.brand_name} {computer.model_name} - {computer.serial_number} 
                              {computer.assigned_user_name ? ` (${computer.assigned_user_name})` : ' (Atanmamış)'}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {recordData.device_type === 'mobile_phone' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Cep Telefonu Seçin *
                      </label>
                      <div className="space-y-2">
                        <input
                          type="text"
                          placeholder="Cep telefonu ara..."
                          value={mobilePhoneSearchTerm}
                          onChange={(e) => setMobilePhoneSearchTerm(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                        <select
                          value={recordData.selected_mobile_phone_id}
                          onChange={(e) => {
                            const phoneId = parseInt(e.target.value);
                            const phone = mobilePhones.find(p => p.id === phoneId);
                            if (phone) {
                              setRecordData({
                                ...recordData,
                                selected_mobile_phone_id: phoneId,
                                device_name: `${phone.brand || ''} ${phone.model || ''}`.trim(),
                                device_brand: phone.brand || '',
                                device_model: phone.model || '',
                                serial_number: phone.imei1 || phone.imei2 || '',
                                inventory_number: phone.inventory_number || '',
                                purchase_date: phone.purchase_date || '',
                                purchase_price: phone.purchase_price || 0
                              });
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <option value={0}>Cep telefonu seçin</option>
                          {mobilePhones
                            .filter(p => recordData.responsible_person_id === 0 || p.assigned_user_id === recordData.responsible_person_id)
                            .filter(phone => 
                              mobilePhoneSearchTerm === '' ||
                              (phone.brand && phone.brand.toLowerCase().includes(mobilePhoneSearchTerm.toLowerCase())) ||
                              (phone.model && phone.model.toLowerCase().includes(mobilePhoneSearchTerm.toLowerCase())) ||
                              (phone.imei1 && phone.imei1.toLowerCase().includes(mobilePhoneSearchTerm.toLowerCase())) ||
                              (phone.imei2 && phone.imei2.toLowerCase().includes(mobilePhoneSearchTerm.toLowerCase())) ||
                              (phone.inventory_number && phone.inventory_number.toLowerCase().includes(mobilePhoneSearchTerm.toLowerCase())) ||
                              (phone.assigned_user_name && phone.assigned_user_name.toLowerCase().includes(mobilePhoneSearchTerm.toLowerCase()))
                            )
                            .map((phone) => (
                            <option key={phone.id} value={phone.id}>
                              {phone.brand} {phone.model} - {phone.imei1 || phone.imei2 || 'IMEI Yok'}
                              {phone.assigned_user_name ? ` (${phone.assigned_user_name})` : ' (Atanmamış)'}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {(recordData.device_type && !['pc', 'mobile_phone'].includes(recordData.device_type)) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Cihaz Adı *
                      </label>
                      <input
                        type="text"
                        value={recordData.device_name}
                        onChange={(e) => setRecordData({...recordData, device_name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="Cihaz adını girin"
                      />
                    </div>
                  )}

                  {(recordData.device_type && !['pc', 'mobile_phone'].includes(recordData.device_type)) && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Marka
                        </label>
                        <input
                          type="text"
                          value={recordData.device_brand}
                          onChange={(e) => setRecordData({...recordData, device_brand: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          placeholder="Dell"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Model
                        </label>
                        <input
                          type="text"
                          value={recordData.device_model}
                          onChange={(e) => setRecordData({...recordData, device_model: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          placeholder="Latitude 3000"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Seri Numarası
                        </label>
                        <input
                          type="text"
                          value={recordData.serial_number}
                          onChange={(e) => setRecordData({...recordData, serial_number: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          placeholder="DL3000-2024-001"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Envanter Numarası
                        </label>
                        <input
                          type="text"
                          value={recordData.inventory_number}
                          onChange={(e) => setRecordData({...recordData, inventory_number: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          placeholder="4240"
                        />
                      </div>
                    </>
                  )}

                  {['pc', 'mobile_phone'].includes(recordData.device_type) && (
                    <div className="md:col-span-2 p-4 bg-primary/10 dark:bg-primary/20 rounded-lg border border-primary/30 dark:border-primary-dark">
                      <div className="flex items-center gap-2 text-primary-dark dark:text-accent text-sm">
                        <Info className="w-4 h-4" />
                        <span className="font-medium">Seçilen Cihaz Bilgileri:</span>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Cihaz Adı:</span>
                          <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{recordData.device_name || '-'}</span>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Marka:</span>
                          <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{recordData.device_brand || '-'}</span>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Model:</span>
                          <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{recordData.device_model || '-'}</span>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Seri No:</span>
                          <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{recordData.serial_number || '-'}</span>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Envanter No:</span>
                          <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{recordData.inventory_number || '-'}</span>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Satın Alma:</span>
                          <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{recordData.purchase_date || '-'}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Satın Alma Tarihi
                    </label>
                    <input
                      type="date"
                      value={recordData.purchase_date}
                      onChange={(e) => setRecordData({...recordData, purchase_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Satın Alma Fiyatı
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="number"
                        step="0.01"
                        value={recordData.purchase_price}
                        onChange={(e) => setRecordData({...recordData, purchase_price: parseFloat(e.target.value) || 0})}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="15000"
                      />
                      <select
                        value={recordData.currency}
                        onChange={(e) => setRecordData({...recordData, currency: e.target.value as 'TRY' | 'USD' | 'EUR'})}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="TRY">₺</option>
                        <option value="USD">$</option>
                        <option value="EUR">€</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Incident Details */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Olay Detayları</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Olay Açıklaması *
                    </label>
                    <textarea
                      value={recordData.incident_description}
                      onChange={(e) => setRecordData({...recordData, incident_description: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="Olayın nasıl gerçekleştiğini detaylı olarak açıklayın..."
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Tanık(lar)
                      </label>
                      <input
                        type="text"
                        value={recordData.witness_names}
                        onChange={(e) => setRecordData({...recordData, witness_names: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="Ahmet Yılmaz, Ayşe Kaya"
                      />
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_disabled_employee"
                      checked={recordData.is_disabled_employee}
                      onChange={(e) => setRecordData({...recordData, is_disabled_employee: e.target.checked})}
                      className="w-4 h-4 text-red-600 border-gray-300 dark:border-gray-600 rounded focus:ring-red-500"
                    />
                    <label htmlFor="is_disabled_employee" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      Engelli Personel (Kesintide %50 indirim uygulanır)
                    </label>
                  </div>

                  {recordData.incident_type === 'damage' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Hasar Değerlendirmesi
                        </label>
                        <textarea
                          value={recordData.damage_assessment}
                          onChange={(e) => setRecordData({...recordData, damage_assessment: e.target.value})}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          placeholder="Hasarın detaylı değerlendirmesi..."
                        />
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="is_repairable"
                          checked={recordData.is_repairable}
                          onChange={(e) => setRecordData({...recordData, is_repairable: e.target.checked})}
                          className="w-4 h-4 text-green-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500"
                        />
                        <label htmlFor="is_repairable" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                          Cihaz onarılabilir (İşaretli değilse güncel piyasa fiyatı kullanılır)
                        </label>
                      </div>
                    </div>
                  )}

                  {recordData.incident_type === 'theft' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Polis Rapor Numarası
                      </label>
                      <input
                        type="text"
                        value={recordData.police_report_number}
                        onChange={(e) => setRecordData({...recordData, police_report_number: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="POL-2024-001"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Cost and Insurance */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Maliyet ve Sigorta</h3>

                {/* Insurance Checkbox */}
                <div className="mb-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="has_insurance"
                      checked={recordData.has_insurance}
                      onChange={(e) => setRecordData({...recordData, has_insurance: e.target.checked})}
                      className="w-4 h-4 text-primary border-gray-300 dark:border-gray-600 rounded focus:ring-primary"
                    />
                    <label htmlFor="has_insurance" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      Sigorta var (Sigorta bilgilerini girmek için işaretleyin)
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Tahmini Onarım Maliyeti
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={recordData.estimated_repair_cost}
                      onChange={(e) => setRecordData({...recordData, estimated_repair_cost: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="2500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Değiştirme Maliyeti
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={recordData.replacement_cost}
                      onChange={(e) => setRecordData({...recordData, replacement_cost: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="15000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Güncel Piyasa Fiyatı
                      <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">(Onarılamaz/Çalınma/Kayıp için)</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={recordData.current_market_price}
                      onChange={(e) => setRecordData({...recordData, current_market_price: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="18000"
                    />
                  </div>

                  {/* Insurance fields - only show if has_insurance is checked */}
                  {recordData.has_insurance && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Sigorta Şirketi
                        </label>
                        <input
                          type="text"
                          value={recordData.insurance_company}
                          onChange={(e) => setRecordData({...recordData, insurance_company: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          placeholder="Anadolu Sigorta"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Sigorta Başvuru Numarası
                        </label>
                        <input
                          type="text"
                          value={recordData.insurance_claim_number}
                          onChange={(e) => setRecordData({...recordData, insurance_claim_number: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          placeholder="INS-2024-001"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Otomatik Kesinti Hesaplama */}
              {recordData.purchase_price > 0 && recordData.purchase_date && recordData.device_type && (
                <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-300 rounded-lg p-5 shadow-sm">
                  <h4 className="text-base font-semibold text-orange-900 mb-4 flex items-center">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                    Prosedüre Göre Hesaplanan Kesinti Tutarı
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm mb-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm">
                      <span className="text-gray-600 dark:text-gray-400 text-xs block mb-1">Cihaz Tipi</span>
                      <div className="font-semibold text-gray-900 dark:text-gray-100">
                        {recordData.device_type === 'pc' && 'Bilgisayar'}
                        {recordData.device_type === 'mobile_phone' && 'Cep Telefonu'}
                        {recordData.device_type === 'monitor' && 'Monitör'}
                        {recordData.device_type === 'tablet' && 'Tablet'}
                        {recordData.device_type === 'printer' && 'Yazıcı'}
                        {recordData.device_type === 'other' && 'Diğer'}
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm">
                      <span className="text-gray-600 dark:text-gray-400 text-xs block mb-1">Kullanım Süresi</span>
                      <div className="font-semibold text-gray-900 dark:text-gray-100">
                        {(() => {
                          const today = new Date();
                          const purchase = new Date(recordData.purchase_date);
                          const monthsUsed = Math.floor((today.getTime() - purchase.getTime()) / (1000 * 60 * 60 * 24 * 30));
                          const yearsUsed = Math.floor(monthsUsed / 12);
                          return `${yearsUsed} yıl`;
                        })()}
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm">
                      <span className="text-gray-600 dark:text-gray-400 text-xs block mb-1">Baz Fiyat</span>
                      <div className="font-semibold text-gray-900 dark:text-gray-100">
                        {getCurrencySymbol(recordData.currency || 'TRY')}
                        {(!recordData.is_repairable || recordData.incident_type === 'theft' || recordData.incident_type === 'loss')
                          ? (recordData.current_market_price || recordData.purchase_price || 0).toLocaleString()
                          : (recordData.purchase_price || 0).toLocaleString()}
                        {(!recordData.is_repairable || recordData.incident_type === 'theft' || recordData.incident_type === 'loss') && (
                          <span className="text-xs text-orange-600 block">Piyasa Fiyatı</span>
                        )}
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm">
                      <span className="text-gray-600 dark:text-gray-400 text-xs block mb-1">Kesinti Oranı</span>
                      <div className="font-semibold text-orange-700">
                        {(() => {
                          const today = new Date();
                          const purchase = new Date(recordData.purchase_date);
                          const monthsUsed = Math.floor((today.getTime() - purchase.getTime()) / (1000 * 60 * 60 * 24 * 30));
                          const yearsUsed = Math.floor(monthsUsed / 12);
                          let percentage = 0;

                          if (recordData.device_type === 'pc' || recordData.device_type === 'monitor' ||
                              recordData.device_type === 'printer' || recordData.device_type === 'tablet' ||
                              recordData.device_type === 'other') {
                            if (yearsUsed < 1) percentage = 85;
                            else if (yearsUsed === 1) percentage = 85;
                            else if (yearsUsed === 2) percentage = 70;
                            else if (yearsUsed === 3) percentage = 50;
                            else if (yearsUsed === 4) percentage = 40;
                            else percentage = 30;
                          } else if (recordData.device_type === 'mobile_phone') {
                            if (yearsUsed < 1) percentage = 70;
                            else if (yearsUsed === 1) percentage = 70;
                            else if (yearsUsed === 2) percentage = 40;
                            else if (yearsUsed === 3) percentage = 20;
                            else if (yearsUsed === 4) percentage = 15;
                            else percentage = 10;
                          }

                          return `%${percentage}`;
                        })()}
                      </div>
                    </div>
                  </div>

                  {recordData.is_disabled_employee && (
                    <div className="bg-primary/10 border border-primary/30 rounded-lg p-3 mb-4">
                      <div className="flex items-center text-sm text-primary-dark">
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <strong>Engelli Personel İndirimi:</strong>
                        <span className="ml-1">Kesinti tutarına %50 indirim uygulanacaktır</span>
                      </div>
                    </div>
                  )}

                  <div className="bg-gradient-to-r from-red-100 to-orange-100 rounded-lg p-4 border-2 border-red-300">
                    <div className="flex items-center justify-between">
                      <span className="text-red-800 font-medium">Kullanıcıdan Kesilecek Tutar:</span>
                      <div className="font-bold text-red-600 text-2xl">
                        {getCurrencySymbol(recordData.currency || 'TRY')}{(recordData.calculated_deduction || 0).toLocaleString()}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-white dark:bg-gray-800 rounded-lg border border-orange-200">
                    <div className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
                      <div className="font-semibold text-orange-800 mb-2">Kesinti Hesaplama Kuralları:</div>
                      <div>• <strong>PC/Monitör/Yazıcı/Tablet:</strong> 1.yıl %85, 2.yıl %70, 3.yıl %50, 4.yıl %40, 5+ yıl %30</div>
                      <div>• <strong>Cep Telefonu:</strong> 1.yıl %70, 2.yıl %40, 3.yıl %20, 4.yıl %15, 5+ yıl %10</div>
                      <div>• Onarılamaz, çalınma veya kayıp durumlarında <strong>güncel piyasa fiyatı</strong> kullanılır</div>
                      <div>• Engelli personel için kesinti tutarına <strong>%50 indirim</strong> uygulanır</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Dosya Yükleme */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Dosya Ekleme</h3>
                <div className="space-y-4">
                  {/* Dosya Yükleme Alanı */}
                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.txt"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      className="cursor-pointer flex flex-col items-center"
                    >
                      <Upload className="w-12 h-12 text-gray-400 mb-4" />
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                        Dosya yüklemek için tıklayın
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Fotoğraf, PDF, Word belgesi (Max 10MB per dosya)
                      </span>
                    </label>
                  </div>

                  {/* Yüklenen Dosyalar */}
                  {uploadedFiles.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Yüklenen Dosyalar ({uploadedFiles.length})
                      </h4>
                      <div className="space-y-2">
                        {uploadedFiles.map((file, index) => {
                          const FileIcon = getFileIcon(file.type);
                          return (
                            <div
                              key={index}
                              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border"
                            >
                              <div className="flex items-center">
                                <FileIcon className="w-5 h-5 text-gray-500 dark:text-gray-400 mr-3" />
                                <div>
                                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {file.name}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                {file.type.startsWith('image/') && (
                                  <span className="inline-flex px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                                    Fotoğraf
                                  </span>
                                )}
                                {!file.type.startsWith('image/') && (
                                  <span className="inline-flex px-2 py-1 text-xs bg-primary/20 text-primary-dark rounded-full">
                                    Belge
                                  </span>
                                )}
                                <button
                                  onClick={() => removeFile(index)}
                                  className="p-1 text-red-500 hover:text-red-700 transition-colors"
                                  title="Dosyayı Kaldır"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Dosya Türü Bilgilendirmesi */}
                  <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-primary-dark mb-2">Dosya Yükleme Kuralları</h4>
                    <ul className="text-xs text-primary-dark space-y-1">
                      <li>• <strong>Fotoğraflar:</strong> JPG, PNG, GIF formatları</li>
                      <li>• <strong>Belgeler:</strong> PDF, Word, Text dosyaları</li>
                      <li>• <strong>Maksimum boyut:</strong> Dosya başına 10MB</li>
                      <li>• <strong>Çoklu seçim:</strong> Birden fazla dosya seçebilirsiniz</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Status and Attachments */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Durum ve Ekler</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Durum *
                    </label>
                    <select
                      value={recordData.status}
                      onChange={(e) => setRecordData({...recordData, status: e.target.value as DamageTheftRecord['status']})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      {statusOptions.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={recordData.photos_attached}
                        onChange={(e) => setRecordData({...recordData, photos_attached: e.target.checked})}
                        className="rounded border-gray-300 dark:border-gray-600 text-red-600 shadow-sm focus:border-red-300 focus:ring focus:ring-red-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Fotoğraf eklendi</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={recordData.documents_attached}
                        onChange={(e) => setRecordData({...recordData, documents_attached: e.target.checked})}
                        className="rounded border-gray-300 dark:border-gray-600 text-red-600 shadow-sm focus:border-red-300 focus:ring focus:ring-red-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Belge eklendi</span>
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Notlar
                </label>
                <textarea
                  value={recordData.notes}
                  onChange={(e) => setRecordData({...recordData, notes: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Ek notlar ve açıklamalar..."
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                {editingRecord ? 'Güncelle' : 'Kaydet'}
              </button>
            </div>
          </div>
        </Modal>

      {/* Record Detail Modal */}
      <Modal isOpen={showDetailModal} onClose={() => setShowDetailModal(false)}>
          {selectedRecord && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Hasar/Çalınma Detayları
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {selectedRecord.record_number} - {selectedRecord.device_name}
                  </p>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Record Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Olay Bilgileri</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Kayıt No:</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedRecord.record_number}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Olay Tipi:</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(selectedRecord.incident_type)}`}>
                        {incidentTypeOptions.find(t => t.value === selectedRecord.incident_type)?.label}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Olay Tarihi:</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{new Date(selectedRecord.incident_date).toLocaleDateString('tr-TR')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Durum:</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedRecord.status)}`}>
                        {statusOptions.find(s => s.value === selectedRecord.status)?.label}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Öncelik:</span>
                      <span className={`font-medium ${getPriorityColor(selectedRecord.priority)}`}>
                        {priorityOptions.find(p => p.value === selectedRecord.priority)?.label}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Sorumlu Kişi:</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {allUsers.find(u => u.id === selectedRecord.responsible_person_id)?.name || 'Belirtilmemiş'}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Cihaz Bilgileri</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Cihaz:</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedRecord.device_name}</span>
                    </div>
                    {selectedRecord.device_brand && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Marka:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{selectedRecord.device_brand}</span>
                      </div>
                    )}
                    {selectedRecord.device_model && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Model:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{selectedRecord.device_model}</span>
                      </div>
                    )}
                    {selectedRecord.serial_number && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Seri No:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{selectedRecord.serial_number}</span>
                      </div>
                    )}
                    {selectedRecord.inventory_number && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Envanter No:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{selectedRecord.inventory_number}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Incident Description */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Olay Açıklaması</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  {selectedRecord.incident_description}
                </p>
              </div>

              {/* Damage Assessment */}
              {selectedRecord.damage_assessment && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Hasar Değerlendirmesi</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg">
                    {selectedRecord.damage_assessment}
                  </p>
                </div>
              )}

              {/* Cost Information */}
              {(selectedRecord.estimated_repair_cost || selectedRecord.replacement_cost) && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Maliyet Bilgileri</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedRecord.estimated_repair_cost && (
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                        <div className="text-sm text-yellow-700 dark:text-yellow-300 mb-1">Tahmini Onarım Maliyeti</div>
                        <div className="text-2xl font-bold text-yellow-600">
                          {getCurrencySymbol(selectedRecord.currency || 'TRY')}{(selectedRecord.estimated_repair_cost || 0).toLocaleString()}
                        </div>
                      </div>
                    )}
                    {selectedRecord.replacement_cost && (
                      <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                        <div className="text-sm text-red-700 dark:text-red-300 mb-1">Değiştirme Maliyeti</div>
                        <div className="text-2xl font-bold text-red-600">
                          {getCurrencySymbol(selectedRecord.currency || 'TRY')}{(selectedRecord.replacement_cost || 0).toLocaleString()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Insurance and Legal */}
              {(selectedRecord.insurance_claim_number || selectedRecord.police_report_number) && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Sigorta ve Hukuki</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    {selectedRecord.insurance_company && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Sigorta Şirketi:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{selectedRecord.insurance_company}</span>
                      </div>
                    )}
                    {selectedRecord.insurance_claim_number && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Sigorta Başvuru No:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{selectedRecord.insurance_claim_number}</span>
                      </div>
                    )}
                    {selectedRecord.police_report_number && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Polis Rapor No:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{selectedRecord.police_report_number}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedRecord.notes && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Notlar</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    {selectedRecord.notes}
                  </p>
                </div>
              )}

              {/* Dosya Bilgileri */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Ek Dosyalar</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Fotoğraf:</span>
                      <span className={`font-medium ${selectedRecord.photos_attached ? 'text-green-600' : 'text-gray-400'}`}>
                        {selectedRecord.photos_attached ? 'Mevcut' : 'Yok'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Belge:</span>
                      <span className={`font-medium ${selectedRecord.documents_attached ? 'text-green-600' : 'text-gray-400'}`}>
                        {selectedRecord.documents_attached ? 'Mevcut' : 'Yok'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Otomatik Kesinti Gösterimi */}
                {selectedRecord.purchase_price && selectedRecord.purchase_date && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Kesinti Hesaplama</h3>
                    <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-orange-700 dark:text-orange-300">Satın Alma:</span>
                          <span className="font-medium">
                            {getCurrencySymbol(selectedRecord.currency || 'TRY')}{(selectedRecord.purchase_price || 0).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-orange-700 dark:text-orange-300">Kullanıcıdan Kesinti:</span>
                          <span className="font-bold text-red-600">
                            {(() => {
                              const purchasePrice = selectedRecord.purchase_price || 0;
                              const purchaseDate = selectedRecord.purchase_date;
                              
                              if (!purchaseDate || purchasePrice === 0) return '₺0';
                              
                              const today = new Date();
                              const purchase = new Date(purchaseDate);
                              const monthsUsed = Math.floor((today.getTime() - purchase.getTime()) / (1000 * 60 * 60 * 24 * 30));
                              const depreciationMonths = Math.min(monthsUsed, 36);
                              const depreciationRate = depreciationMonths / 36;
                              const depreciatedValue = purchasePrice * (1 - depreciationRate);
                              
                              let deductionRate = 0;
                              switch (selectedRecord.incident_type) {
                                case 'damage': deductionRate = 0.25; break;
                                case 'theft': deductionRate = 0.50; break;
                                case 'loss': deductionRate = 0.75; break;
                              }
                              
                              const deduction = Math.round(depreciatedValue * deductionRate);
                              return getCurrencySymbol(selectedRecord.currency || 'TRY') + (deduction || 0).toLocaleString();
                            })()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Attachments */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Ekler</h3>
                <div className="flex space-x-4">
                  {selectedRecord.photos_attached && (
                    <div className="flex items-center px-3 py-2 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-lg">
                      <Camera className="w-4 h-4 mr-2" />
                      <span className="text-sm">Fotoğraflar mevcut</span>
                    </div>
                  )}
                  {selectedRecord.documents_attached && (
                    <div className="flex items-center px-3 py-2 bg-primary/10 dark:bg-primary/20 text-primary-dark dark:text-accent rounded-lg">
                      <Paperclip className="w-4 h-4 mr-2" />
                      <span className="text-sm">Belgeler mevcut</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Timestamps */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  <span>Oluşturan:</span>
                  <div className="font-medium">{selectedRecord.created_by_user?.name}</div>
                  <div>{new Date(selectedRecord.created_at).toLocaleString('tr-TR')}</div>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  <span>Son Güncelleme:</span>
                  <div className="font-medium">{new Date(selectedRecord.updated_at).toLocaleString('tr-TR')}</div>
                </div>
              </div>
            </div>
          </div>
          )}
        </Modal>

      {/* Quick User Creation Modal */}
      <Modal isOpen={showUserModal} onClose={() => setShowUserModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md max-h-[85vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Hızlı Kullanıcı Oluştur
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Yeni kullanıcı oluşturup sorumlu kişi olarak atayın
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Ad Soyad *
                </label>
                <input
                  type="text"
                  value={newUserData.name}
                  onChange={(e) => setNewUserData({...newUserData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Mehmet Yılmaz"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  E-posta *
                </label>
                <input
                  type="email"
                  value={newUserData.email}
                  onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="mehmet@company.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Telefon *
                </label>
                <input
                  type="text"
                  value={newUserData.phone}
                  onChange={(e) => setNewUserData({...newUserData, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="+90 532 123 45 67"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Ünvan
                </label>
                <input
                  type="text"
                  value={newUserData.title}
                  onChange={(e) => setNewUserData({...newUserData, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Muhasebe Uzmanı"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Departman
                </label>
                <input
                  type="text"
                  value={newUserData.department}
                  onChange={(e) => setNewUserData({...newUserData, department: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Muhasebe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Lokasyon
                </label>
                <select
                  value={newUserData.location_id}
                  onChange={(e) => setNewUserData({...newUserData, location_id: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value={0}>Lokasyon seçin</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowUserModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleSaveUser}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                Oluştur ve Ata
              </button>
            </div>
          </div>
        </Modal>
    </div>
  );
};

export default DamageTheftPage;